import { ILayer } from '../layer.type';
import { environment } from '~environments/environment';
import { _ } from '~shared/utils';
import { generateDigitrafficTimeParam, getSharedLayer } from './shared';
import { Expression } from 'mapbox-gl';
import { MarkerMode } from '~shared/map/map.service';
import { TenantConfig } from './tenant-config.type';

const ZOOM_LEVEL_EQUIPMENT = 15;

const TRAIN_DEFAULT_ORDERING_WEIGHT = -1100;
const EQUIPMENT_DEFAULT_ORDERING_WEIGHT = TRAIN_DEFAULT_ORDERING_WEIGHT + 1;
/** Already translated */
const VrLayerGroupNames = {
  TRAINS: _('Trains'),
  EQUIPMENT: _('Equipment'),
  RAIL_INFRASTRUCTURE: _('Rail infrastructure')
} as const;

const markerUrlBase = environment.assetRoot + '/data/vr/markers/';
export const images = [
  { id: 'success-marker', url: markerUrlBase + 'success-marker.png' },
  { id: 'info-marker', url: markerUrlBase + 'info-marker.png' },
  { id: 'danger-marker', url: markerUrlBase + 'danger-marker.png' },
  { id: 'warning-marker', url: markerUrlBase + 'warning-marker.png' },
  { id: 'gray-marker', url: markerUrlBase + 'gray-marker.png' },
  { id: 'success-stopped-marker', url: markerUrlBase + 'success-stopped-marker.png' },
  { id: 'info-stopped-marker', url: markerUrlBase + 'info-stopped-marker.png' },
  { id: 'danger-stopped-marker', url: markerUrlBase + 'danger-stopped-marker.png' },
  { id: 'warning-stopped-marker', url: markerUrlBase + 'warning-stopped-marker.png' },
  { id: 'gray-stopped-marker', url: markerUrlBase + 'gray-stopped-marker.png' },
];

const defaultMarkerIconUrl = markerUrlBase + 'success-marker.png';

const iconUrlExpressionRunning: Expression = [
  'match', ['get', 'status'],
  'info', 'info-marker',
  'danger', 'danger-marker',
  'warning', 'warning-marker',
  'success-marker'
];

const iconUrlExpressionStopped: Expression = [
  'match', ['get', 'status'],
  'success', 'success-stopped-marker',
  'info', 'info-stopped-marker',
  'danger', 'danger-stopped-marker',
  'warning', 'warning-stopped-marker',
  'success-stopped-marker'
];

const iconUrlExpression: Expression = [
  'case',
  ['==', ['get', 'velocity'], 0], iconUrlExpressionStopped,
  iconUrlExpressionRunning
];

const equipmentIconUrlExpression: Expression = [
  'case',
  ['==', ['get', 'velocity'], 0], 'gray-stopped-marker',
  'gray-marker'
];

const iconRotationExpression: Expression = [
  'case',
  ['==', ['get', 'velocity'], 0], 0,
  ['+', ['get', 'bearing'], 0]
];

// Place text to the right side of the train, using eight 45 degree sectors.
// visual: https://docs.mapbox.com/help/troubleshooting/optimize-map-label-placement/#label-density
const trainTextLayout: mapboxgl.SymbolLayout = {
  'text-allow-overlap': true,
  'text-anchor': [
    'case',
    ['<', ['get', 'bearing'], 45 * 1 - 45 / 2], 'left',
    ['<', ['get', 'bearing'], 45 * 2 - 45 / 2], 'top-left',
    ['<', ['get', 'bearing'], 45 * 3 - 45 / 2], 'top',
    ['<', ['get', 'bearing'], 45 * 4 - 45 / 2], 'top-right',
    ['<', ['get', 'bearing'], 45 * 5 - 45 / 2], 'right',
    ['<', ['get', 'bearing'], 45 * 6 - 45 / 2], 'bottom-right',
    ['<', ['get', 'bearing'], 45 * 7 - 45 / 2], 'bottom',
    ['<', ['get', 'bearing'], 45 * 8 - 45 / 2], 'bottom-left',
    'left', // anything left over is the counter clockwise half of the top sector
  ],
  'text-justify': [
    'case',
    ['<', ['get', 'bearing'], 45 * 1 - 45 / 2], 'left',
    ['<', ['get', 'bearing'], 45 * 2 - 45 / 2], 'left',
    ['<', ['get', 'bearing'], 45 * 3 - 45 / 2], 'center',
    ['<', ['get', 'bearing'], 45 * 4 - 45 / 2], 'right',
    ['<', ['get', 'bearing'], 45 * 5 - 45 / 2], 'right',
    ['<', ['get', 'bearing'], 45 * 6 - 45 / 2], 'right',
    ['<', ['get', 'bearing'], 45 * 7 - 45 / 2], 'center',
    ['<', ['get', 'bearing'], 45 * 8 - 45 / 2], 'left',
    'left', // anything left over is the counter clockwise half of the top sector
  ],
};

export interface VRTenantConfig extends TenantConfig {
  tenantCustomData: {
    urls: {
      challenge: string,
    },
  };
}

/**
 * VRGroup
 */
export default (markerMode?: MarkerMode): VRTenantConfig => ({
  layerDefinitions: (() => {
    switch (markerMode) {
      case MarkerMode.Canvas:
        return getCanvasLayers();
      case MarkerMode.HTML:
        return getHtmlLayers();
      default:
        return getHtmlLayers();
    }
  })(),
  images,
  hasMultipleMarkerModes: true,
  defaultCenter: {
    lat: 62.404,
    lng: 25.940,
  },
  defaultZoom: 6.00,
  alarmFilters: [{
    id: 2,
    name: _('Type'),
    type: 'dropdown-multiple',
    noFiltersShowsAll: true,
    filter_values: [
      {
        name: _('Equipment conflict'),
        value: 'vrTrainConflict',
        boolean: true
      },
      {
        name: _('Personnel conflict'),
        value: 'vrShiftConflict',
        boolean: true
      },
      {
        name: _('Train departure'),
        value: 'trainDeparture',
        boolean: true
      },
      {
        name: _('Train stopped'),
        value: 'vrTrainStopped',
        boolean: true
      },
      {
        name: _('Overheating alert'),
        value: 'vrOverheating',
        boolean: true
      },
      {
        name: _('Wheel alert'),
        value: 'vrWheelAlert',
        boolean: true
      },
      {
        name: _('Voltage alert'),
        value: 'vrVoltageAlert',
        boolean: true
      }
    ]
  }, {
    id: 6,
    name: _('Class'),
    type: 'dropdown-multiple',
    noFiltersShowsAll: true,
    filter_values: [
      {
        name: _('Freight'),
        value: 'FREIGHT',
        boolean: true
      },
      {
        name: _('Long-distance'),
        value: 'LONGDISTANCE',
        boolean: true
      },
      {
        name: _('Commuter'),
        value: 'COMMUTER',
        boolean: true
      },
      {
        name: _('Locomotive'),
        value: 'LOCOMOTIVE',
        boolean: true
      },
      {
        name: _('Shunting'),
        value: 'SHUNTING',
        boolean: true
      },
      {
        name: _('Others'),
        value: 'OTHER',
        boolean: true
      },
      {
        name: _('Unclassified'),
        value: 'UNCLASSIFIED',
        boolean: true
      }
    ]
  }],
  tenantCustomData: {
    urls: {
      challenge: 'https://tuotanto.rata.liikenteenohjaus.fi/kutiv2/aikataulupoikkeamat',
    }
  },
  renderFlagFilteringForLayers: false
});

function getCanvasLayers(): ILayer[] {
  return [
    {
      id: 'Kaukojunat',
      name: _('Long-distance'),
      belowTitles: false,
      title: 'Junat',
      orderingWeight: TRAIN_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: iconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true,
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10, // === 14km,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        priority: 10,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true
    },
    {
      id: 'Lähijunat',
      name: _('Commuter'),
      belowTitles: false,
      title: 'Junat',
      orderingWeight: TRAIN_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: iconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 12, // === 2km
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        priority: 5,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true
    },
    {
      id: 'Muut',
      name: _('Others'),
      belowTitles: false,
      title: 'Junat',
      orderingWeight: TRAIN_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: iconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10, // === 2km
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true
    },
    {
      id: 'TR',
      name: 'TR',
      belowTitles: false,
      title: 'Junat',
      orderingWeight: TRAIN_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: iconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: ['concat', ['get', 'title'], ' ', ['get', 'subTitle']],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10, // === 2km
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true,
    },
    {
      id: 'TL',
      name: 'TL',
      belowTitles: false,
      title: 'Junat',
      orderingWeight: TRAIN_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: iconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10, // === 2km
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true,
    },
    {
      id: 'TA',
      name: 'TA',
      belowTitles: false,
      title: 'Junat',
      orderingWeight: TRAIN_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: iconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10, // === 2km
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true,
    },
    {
      id: 'T',
      name: 'T',
      belowTitles: false,
      title: 'Junat',
      orderingWeight: TRAIN_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: iconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10, // === 2km
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true,
    },
    {
      id: 'Veturit',
      name: _('Locomotive'),
      belowTitles: false,
      title: 'Junat',
      orderingWeight: TRAIN_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: iconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10, // === 2km
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
    },
    {
      id: 'SR1',
      name: 'SR1',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SR2',
      name: 'SR2',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SR3',
      name: 'SR3',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'DV12',
      name: 'DV12',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'DR14',
      name: 'DR14',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'DR16',
      name: 'DR16',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'DM12',
      name: 'DM12',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SM2',
      name: 'SM2',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SM3',
      name: 'SM3',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SM4',
      name: 'SM4',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SM5',
      name: 'SM5',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SM6',
      name: 'SM6',
      belowTitles: false,
      title: 'Kalusto',
      orderingWeight: EQUIPMENT_DEFAULT_ORDERING_WEIGHT,
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        icon: {
          urlExpression: equipmentIconUrlExpression,
          url: defaultMarkerIconUrl,
          colorable: true,
          rotation: iconRotationExpression,
          textField: [
            'concat',
            ['get', 'title'],
            ['get', 'subTitle']
          ],
        },
        layerType: 'symbol',
        sourceType: 'geojson',
        visible: true
      },
      mapboxOptions: {
        layout: trainTextLayout,
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT,
        flaggingDisabled: true
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    getSharedLayer('levelcrossings'),
    getSharedLayer('rails'),
    getSharedLayer('trafficControlPoints'),
    getSharedLayer('intersectionStations'),
    ...getRailwayInfra()
  ];
}

function getHtmlLayers(): ILayer[] {
  const timeParam = encodeURIComponent(generateDigitrafficTimeParam());
  return [
    {
      id: 'Kaukojunat',
      name: _('Long-distance'),
      title: 'Junat',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: true
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10 // === 14km
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        priority: 10,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true
    },
    {
      id: 'Lähijunat',
      name: _('Commuter'),
      title: 'Junat',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: true
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 12 // === 2km
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        priority: 5,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true
    },
    {
      id: 'Muut',
      name: _('Others'),
      title: 'Junat',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: true
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10 // === 2km
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true
    },
    {
      id: 'TR',
      name: 'TR',
      title: 'Junat',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: true
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10 // === 2km
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true,
    },
    {
      id: 'TL',
      name: 'TL',
      title: 'Junat',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: true
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10 // === 2km
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true,
    },
    {
      id: 'TA',
      name: 'TA',
      title: 'Junat',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: true
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10 // === 2km
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true,
    },
    {
      id: 'T',
      name: 'T',
      title: 'Junat',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: true
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10 // === 2km
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
      useWorker: true,
    },
    {
      id: 'Veturit',
      name: _('Locomotive'),
      title: 'Junat',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: true
      },
      metadata: {
        group: VrLayerGroupNames.TRAINS,
        component: 'TrainInfoComponent',
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['tripNumber', 'category', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: 10 // === 2km
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'tripNumber', type: 'startsWith' },
          { key: 'category', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'equipment', type: 'equals', reason: _('attached locomotive matches'), priority: -1 }
        ],
      },
    },
    {
      id: 'SR1',
      name: 'SR1',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SR2',
      name: 'SR2',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SR3',
      name: 'SR3',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'DV12',
      name: 'DV12',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'DR14',
      name: 'DR14',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'DR16',
      name: 'DR16',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'DM12',
      name: 'DM12',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SM2',
      name: 'SM2',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SM3',
      name: 'SM3',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SM4',
      name: 'SM4',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SM5',
      name: 'SM5',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    {
      id: 'SM6',
      name: 'SM6',
      title: 'Kalusto',
      source: 'trains',
      asMarkers: true,
      selectable: true,
      style: {
        layerType: 'html-marker',
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.EQUIPMENT,
        hoverComponent: 'TrainHover',
        hoverProperties: {
          coordinates: false,
          properties: ['equipment', 'title', 'label']
        },
        uniqueBy: '_id',
        labelBy: 'label',
        detailsComponent: 'train-details',
        zoomLevel: ZOOM_LEVEL_EQUIPMENT
      },
      searchOptions: {
        searchable: true,
        minimumSearchTermLength: 3,
        interestingProperties: [
          { key: 'equipment', type: 'startsWith' },
          { key: 'title', type: 'startsWith' },
          { key: 'label', type: 'startsWith' },
          { key: 'subTitle', type: 'startsWith' }
        ],
      },
      useWorker: true,
    },
    getSharedLayer('levelcrossings'),
    getSharedLayer('rails'),
    getSharedLayer('trafficControlPoints'),
    getSharedLayer('intersectionStations'),
    ...getRailwayInfra()
  ];
}

function getRailwayInfra(): ILayer[] {
  const timeParam = encodeURIComponent(generateDigitrafficTimeParam());
  return [
    {
      id: 'Ratapihat',
      name: _('Railyards'),
      belowTitles: false,
      geojsonURL: environment.assetRoot + '/data/vr/ratapihat.geojson',
      selectable: false,

      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: { url: environment.assetRoot + '/data/vr/ratapiha.png' },
        visible: false
      },
      metadata: {
        group: VrLayerGroupNames.RAIL_INFRASTRUCTURE,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: false,
          properties: ['name']
        },
        uniqueBy: 'name',
        labelBy: 'name',
        url: 'https://kalustopaikannus.vr.fi/ratapihanakyma/',
        urlProperty: 'name',
        urlDescription: _('Open equipment positioning for railyard')
      },
      searchOptions: {
        searchable: false,
        minimumSearchTermLength: 1,
        interestingProperties: [
          { key: 'name', type: 'contains' }
        ],
      },
      useWorker: true,
      orderingWeight: -1,
    },
    {
      id: 'Kuumakäynti-ilmaisimet',
      name: _('Hot-box detectors'),
      belowTitles: false,
      selectable: true,
      geojsonURL: `https://rata.digitraffic.fi/infra-api/0.2/elementit.geojson?srsName=crs%3A84&time=${timeParam}&typeNames=kuumakayntiilmaisin`,
      metadata: {
        group: VrLayerGroupNames.RAIL_INFRASTRUCTURE,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: false,
          properties: ['nimi', 'tunniste', 'tyyppi']
        },
        uniqueBy: 'tunniste',
        labelBy: 'nimi',
      },
      searchOptions: {
        searchable: false,
        minimumSearchTermLength: 2,
        interestingProperties: [
          { key: 'nimi', type: 'contains' },
          { key: 'tunniste', type: 'contains' },
          { key: 'tyyppi', type: 'contains' }
        ],
      },
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        visible: false,
        icon: { url: environment.assetRoot + '/data/vr/kuumakaynti.png' },
      },
      orderingWeight: 0,
      useWorker: true,
    },
    {
      id: 'KM markers',
      name: _('KM markers'),
      vectorURL: 'mapbox://mipro-dev.4ssmf4wd',
      vectorSourceLayer: 'kilometrimerkit_4326-8u84ds',
      orderingWeight: 1000,
      mapboxOptions: {
        paint: {
          'text-color': 'black',
          'text-halo-color': 'hsl(35, 8%, 85%)',
          'text-halo-width': 0.5,
        },
        layout: {
          'icon-image': 'house_number_plate',
          'icon-size': 0.85,
          'icon-offset': [15, 0],
          'text-field': ['get', 'ratakm'],
          'text-font': ['Arial Unicode MS Regular'],
          'text-max-width': 7,
          'text-padding': 4,
          'text-size': 11,
          'text-offset': [1.0, 0.0]
        },
      },
      metadata: {
        group: VrLayerGroupNames.RAIL_INFRASTRUCTURE,
        labelBy: 'ratakm',
        uniqueBy: 'tunniste',
        visibleByZoom: 10,
        //component: 'ElementDetailsComponent',
        //hoverComponent: 'ElementDetailsHover',
        //hoverProperties: {
        //  coordinates: false,
        //  properties: ['ratakm', 'pituus', 'ratanumero', 'rata', 'tunniste', 'seuraavaKilometrimerkki']
        //},
      },
      searchOptions: {
        searchable: false
      },
      style: {
        sourceType: 'vector',
        layerType: 'symbol',
        //clickable: true,
        visible: true,
        icon: {
          url: environment.assetRoot + '/img/house_number_plate.png',
        },
      },
    },
    {
      id: 'Pyörävoimailmaisin',
      name: _('Wheel strength indicator'),
      geojsonURL: `https://rata.digitraffic.fi/infra-api/0.2/elementit.geojson?srsName=crs%3A84&time=${timeParam}&typeNames=pyoravoimailmaisin`,
      belowTitles: false,
      metadata: {
        group: VrLayerGroupNames.RAIL_INFRASTRUCTURE,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: false,
          properties: ['nimi', 'tunniste', 'tyyppi']
        },
        uniqueBy: 'tunniste',
        labelBy: 'nimi',
      },
      searchOptions: {
        searchable: false,
        minimumSearchTermLength: 2,
        interestingProperties: [
          { key: 'nimi', type: 'contains' },
          { key: 'tunniste', type: 'contains' },
          { key: 'tyyppi', type: 'contains' }
        ],
      },
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        visible: false,
        icon: { url: environment.assetRoot + '/data/vr/pyoravoima.png' },
      },
      useWorker: true,
    },
    {
      id: 'Virroitinvalvontakamera',
      name: _('Impulse control camera'),
      geojsonURL: `https://rata.digitraffic.fi/infra-api/0.2/elementit.geojson?srsName=crs%3A84&time=${timeParam}&typeNames=virroitinvalvontakamera`,
      belowTitles: false,
      metadata: {
        group: VrLayerGroupNames.RAIL_INFRASTRUCTURE,
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          coordinates: false,
          properties: ['nimi', 'tunniste', 'tyyppi']
        },
        uniqueBy: 'tunniste',
        labelBy: 'nimi',
      },
      searchOptions: {
        searchable: false,
        minimumSearchTermLength: 2,
        interestingProperties: [
          { key: 'nimi', type: 'contains' },
          { key: 'tunniste', type: 'contains' },
          { key: 'tyyppi', type: 'contains' }
        ],
      },
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        visible: false,
        icon: { url: environment.assetRoot + '/data/vr/virroitinvalvonta.png' },
      },
      orderingWeight: 0,
      useWorker: true,
    }
  ];
}
